import { autoGenerateAfterSave } from '@/lib/project-save';
import { NodeLayout } from '@/components/nodes/layout';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Zap } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { OptionRemark } from '@/components/nodes/shared/option-remark';
import { handleError } from '@/lib/error/handle';
import { download } from '@/lib/download';
import { resolveDownloadableUrl } from '@/lib/nodes/asset-resolve';
import { readImageSizeFromUrl } from '@/lib/image/read-image-size';
import { patchHistoryItemById } from '@/components/nodes/shared/media-history';
import { imageModels, getEffectiveResolutions, getEffectiveImageConfig, getEffectiveImagePrecheck } from '@flowmuse/models/image';
import { resolveImageHistorySize } from '@flowmuse/models/image/tools';
import { getDefaultModelId, getLastSelectedModelId, getPresetAspectRatio, getPresetModelId, getPresetModelParams, rememberLastSelectedModelId } from '@flowmuse/models/shared/utils';
import { buildProviderOptions, getUiOptionsForModel, type UiOption } from '@flowmuse/models/image/ui-options';
import {
  getAllImagesFromNodes,
  getDescriptionsFromImageNodes,
  getImagesFromImageNodes,
  getPromptFromToolNodes,
  getPromptFromUploadNodes,
  getTextFromTextNodes,
} from '@/lib/xyflow';
import { useProject } from '@/providers/project';
import { useSubscription, filterModelsClient } from '@/providers/subscription';
import { markIncompatibleModels } from '@/components/nodes/model-access';
import { useEstimatedBilling } from '@/hooks/use-estimated-credits';
import { env } from '@/lib/env';
import { cn } from '@/lib/utils';
import { isPregeneratedThumbnailUrl, resolveThumbnailDownloadUrl, resolveThumbnailDownloadUrls, useImageVariant } from '@/lib/image-variant';
import { VariantImage } from '@/components/variant-image';
import { estimateImageUsdCost, estimateImagePassCount } from '@flowmuse/models/cost/image-estimate';
import { useNodeConnections, useReactFlow, useStore } from '@xyflow/react';
import { shallow } from 'zustand/shallow';
import { useInViewport } from '@/hooks/use-in-viewport';
import { useNodeOperations } from '@/providers/node-operations';
import { useImageGuidance } from './use-image-guidance';
import { useImageGuidanceActions } from './use-image-guidance-actions';
import { ImageGuidance } from './image-guidance';
import {
  ClockIcon,
  DownloadIcon,
  ImageIcon,
  Loader2Icon,
  RotateCcwIcon,
    Dice5Icon,
    FileText,
    Pencil,
    Sparkles,
    Scissors,
    Paintbrush,
    ScanSearch,
    Package,
    Maximize2,
} from 'lucide-react';
import Image from 'next/image';
import { ImageComparePreview } from '@/components/nodes/image/image-compare-preview';
import { EditWithPinturaOnDoubleClick } from '@/components/nodes/image/pintura-edit-on-double-click';
import { PreviewImageOnClick } from '@/components/nodes/image/preview-on-click';
import { ImagePreviewDialog } from '@/components/nodes/image/preview-dialog';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';
import { useAuth, useUser } from '@clerk/nextjs';
import { uploadFile } from '@/lib/upload';
import {
  Fragment,
  memo,
  type ChangeEventHandler,
  type ComponentProps,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { toast } from 'sonner';
import { mutate } from 'swr';
import type { ImageNodeProps } from '.';
import { ImageSizeSelector } from './image-size-selector';
import { InlineComboboxSelect } from './inline-combobox-select';
import { ImageResolutionSelector } from './image-resolution-selector';
import { ModelItem, SizeItem, GenerateItem, DownloadItem, HistoryItem, NodeSurface, ProcessingSurface, useLinearProgress, InlineOptionsRow } from '@/components/nodes/shared';
import { Input } from '@/components/ui/input';
import { ModelSelector } from '../model-selector';
import { ModeChip } from '@/components/ui/mode-chip';
import { ThreeRowToolbar, InstructionInput, type InstructionInputHandle, AdvancedToggleButton, AdvancedInlinePanel, useIncomingImageInputs, PrecheckDialog, TextModeImageDialog } from '@/components/nodes/shared';
import {
  type InlineAttachment,
  type InlineMediaConfig,
  detectMediaType,
  parseBytes,
  canAddMore,
  buildAcceptFromConfig,
} from '@flowmuse/models/inline-attachments';
import {
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { DraggableMediaGrid, type MediaGridItem } from '@/components/nodes/shared/draggable-media-grid';
import { rewriteMentionsAfterRemoval, rewriteMentionsAfterReorder, type MentionLabels } from '@/components/nodes/shared/mention-mirror';
import { useDeferredInstructionsRewrite } from '@/components/nodes/shared/use-deferred-instructions-rewrite';
import { useFlushInstructionsOnUnmount } from '@/components/nodes/shared/use-flush-instructions-on-unmount';
import { SubjectPickerDialog } from '@/components/nodes/shared/subject-picker-dialog';
import { useI18n } from '@/providers/i18n';
// Phase 9.7: per-id selectors replaced with a single inline shallow-equal
// store slice; old selectors no longer needed here.
import { useIsMultiSelect } from '@/components/canvas/utils/multi-select-store';
import { createImageHandleGenerate } from '@/components/nodes/image/handle-generate';
import { cancelGeneration } from '@/lib/cancel-generation';
import { useImageRealtime } from '@/components/nodes/image/handle-realtime';
import { useImagePolling } from '@/components/nodes/image/handle-polling';
import { useAutoCancelHandler, useAutoGenerateHandler } from '@/components/canvas/utils/auto-generate-bus';
import { nanoid } from 'nanoid';
import { describeAction } from '@/app/actions/image/describe';
import { DescriptionDialog } from './description-dialog';
import { ImageTopToolbar } from './image-top-toolbar';
import { ImageCollapsedSummary } from '@/components/nodes/image/collapsed-summary';
import { ImagePluginButton } from './plugin-button';
import { GlbViewer } from '@/components/nodes/model/glb-viewer';
import { MultiImageTrigger, useMultiImagePicker } from './multi-image-picker';
import { requestLibraryPick, type LibraryPickResult } from '@/components/canvas/utils/asset-folders-events';
import { resolveAssetRefPreview } from '@flowmuse/models/shared/asset-ref';

type ImageTransformProps = ImageNodeProps & {
  title: string;
};


export const ImageTransform = memo(({
  data,
  id,
  type,
  title,
}: ImageTransformProps) => {
  const { t } = useI18n();
  const isPreview = usePreviewCanvas();
  const { updateNodeData, getNodes, getEdges, getNode, addEdges, setNodes, setEdges, setCenter, getViewport } = useReactFlow();
  const { addNode } = useNodeOperations();
  const [loading, setLoading] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const generatedUrl = (data as any)?.generated?.url as string | undefined;
  const generatedUrls = (data as any)?.generated?.urls as string[] | undefined;
  const assetPreviewUrl = (data as any)?.assetPreviewUrl as string | undefined;
  const effectiveGeneratedUrl = resolveAssetRefPreview(generatedUrl, assetPreviewUrl);
  const status = (data as any)?.status as string | undefined;
  const isProcessing = status === 'processing';
  const isFailed = status === 'failed';
  const [previewOpen, setPreviewOpen] = useState(false);
  // Phase 9.6: removed `isHovered` React state — was triggering a full node
  // re-render every time the mouse entered/left the output area, which on a
  // canvas with many image nodes meant a re-render cascade just from cursor
  // movement. The visual hover feedback (1px inset shift) was minor and is
  // dropped in favor of the selected-state inset toggle below.
  const [instructionsFocused, setInstructionsFocused] = useState(false);
  // 本地状态存储补充说明输入框的值，失去焦点后再保存
  const [localInstructions, setLocalInstructions] = useState(data.instructions ?? '');
  const defaultNodeStyle = ((env.NEXT_PUBLIC_DEFAULT_NODE_STYLE ?? 'none') === 'custom') ? 'custom' : 'none';
  const [selectedStyle, setSelectedStyle] = useState((data as any).style || defaultNodeStyle);
  const { isSubscribed, modelAccessMap, modelPassMap, userPlan, userInterval, userTags, planLevels, consumeModelPass } = useSubscription();
  // 默认模型按「当前用户过滤后」的模型表解析：高优先级 default（如仅 international
  // 标签可见的国际版）不可见时，getDefaultModelId 自然回退到低优先级兜底。
  const accessFilteredImageModels = useMemo(
    () => filterModelsClient(imageModels, modelAccessMap, userPlan, userInterval, userTags, modelPassMap, planLevels),
    [modelAccessMap, modelPassMap, userPlan, userInterval, userTags, planLevels],
  );
  const modelId =
    data.model ??
    getPresetModelId('image', imageModels) ??
    getLastSelectedModelId('image', imageModels) ??
    getDefaultModelId(accessFilteredImageModels);
  const selectedModel = imageModels[modelId];

  // 按当前模式（t2i / i2i）合并模型配置：configByMode[mode] 覆盖顶层（仿照 video 的 getEffectiveConfig）
  const modeCfg = useMemo(
    () => getEffectiveImageConfig(selectedModel, (data as any)?.imageMode as string | undefined),
    [selectedModel, (data as any)?.imageMode]
  );

  const modelUiJson = useMemo(() => {
    try { return JSON.stringify((data as any)?.modelUi ?? null); } catch { return ''; }
  }, [(data as any)?.modelUi]);
  const estimateParams = useMemo(
    () => ({ model: selectedModel as any, nodeData: data as any }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedModel, (data as any)?.size, (data as any)?.imagesCount, (data as any)?.image_resolution, modelUiJson]
  );

  const estimatedUsdCost = useMemo(() => estimateImageUsdCost(estimateParams), [estimateParams]);
  const estimatedPassCount = useMemo(() => estimateImagePassCount(estimateParams), [estimateParams]);

  // passCount 为折后次卡数量（模型卡折扣），仅用于展示/预估；请求侧仍由服务端按原始数量打折实扣。
  const { credits: estimatedCredits, passRemaining, discountMultiplier, passCount, passDiscountMultiplier } = useEstimatedBilling(estimatedUsdCost, modelId, estimatedPassCount);
  const imagesCountOptions = useMemo(() => {
    const opts = modeCfg.imagesCountOptions;
    if (Array.isArray(opts) && opts.length > 0) return Array.from(new Set(opts)).sort((a,b)=>a-b);
    return [1];
  }, [modeCfg]);
  const currentImagesCount = Number((data as any)?.imagesCount ?? imagesCountOptions[0]);
  // 线性进度：优先使用服务端按真实完成记录计算的缓存 P90，未就绪时回退到默认值。
  const { progress, remainingText, hasEstimate } = useLinearProgress({
    isProcessing,
    updatedAt: (data as any)?.updatedAt,
    runtimeEstimatedTime: Number((data as any)?.runtimeEstimatedTime),
    fallbackSeconds: 3 * 60,
  });
  const [isDescribing, setIsDescribing] = useState(false);

  // 回填：节点初次创建且未设置 model 时，将选中的默认模型写回节点数据，并应用预设参数
  useEffect(() => {
    try {
      if (!data.model && modelId) {
        const updates: any = { model: modelId };
        const presetParams = getPresetModelParams(modelId);
        if (presetParams) {
          if (presetParams.size && !(data as any).size) {
            const validSizes = (modeCfg.sizes ?? []) as string[];
            if (validSizes.includes(presetParams.size)) updates.size = presetParams.size;
          }
          if (presetParams.resolution && !(data as any).image_resolution) {
            const validRes = Array.isArray(modeCfg.resolutions) ? modeCfg.resolutions as string[] : [];
            if (validRes.includes(presetParams.resolution)) updates.image_resolution = presetParams.resolution;
          }
          if (presetParams.imagesCount != null && !(data as any).imagesCount) {
            const validCounts = Array.isArray(modeCfg.imagesCountOptions) ? modeCfg.imagesCountOptions : [];
            if (validCounts.includes(Number(presetParams.imagesCount))) updates.imagesCount = Number(presetParams.imagesCount);
          }
        }
        updateNodeData(id, updates);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, modelId, data.model]);

  // Sync selectedStyle with data.style when data changes
  useEffect(() => {
    const dataStyle = (data as any).style || defaultNodeStyle;
    if (dataStyle !== selectedStyle) {
      setSelectedStyle(dataStyle);
    }
  }, [(data as any).style, defaultNodeStyle]);

  // 同步 data.instructions 到本地状态（当外部更新时，但不在用户编辑时）
  useEffect(() => {
    // 如果用户正在编辑，不覆盖本地状态，避免打断输入
    if (!instructionsFocused) {
      setLocalInstructions(data.instructions ?? '');
    }
  }, [data.instructions, instructionsFocused]);

  // 模型变化时，自动约束 imagesCount（默认 1）
  useEffect(() => {
    try {
      const current = Number((data as any)?.imagesCount ?? 1);
      const allowed = imagesCountOptions;
      const next = allowed.includes(current) ? current : allowed[0];
      if (current !== next) {
        updateNodeData(id, { imagesCount: next });
      }
    } catch {}
  }, [id, updateNodeData, modelId, imagesCountOptions]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const instructionInputRef = useRef<InstructionInputHandle>(null);
  const project = useProject();
  const { getToken } = useAuth();
  const { user } = useUser();
  const canUseAdvanced = isSubscribed;
  const [t2iImageDialogOpen, setT2iImageDialogOpen] = useState(false);
  const [precheckMissing, setPrecheckMissing] = useState<'prompt' | 'image' | null>(null);

  // 订阅当前节点的连接，而不是在每次 React Flow store 更新时扫描全量 nodes/edges。
  const incomingConnections = useNodeConnections({ id, handleType: 'target' });
  const incomers = useMemo(
    () =>
      incomingConnections
        .map((connection: any) => getNode(String(connection?.source || '')))
        .filter(Boolean),
    [incomingConnections, getNode]
  );

  // 快捷操作：添加提示词节点和图像节点
  const { handleAddImageNode } = useImageGuidanceActions({ id, t });
  
  const { incomingImageInputs, focusNodeById } = useIncomingImageInputs({ id, t });

  // ── Inline attachments ────────────────────────────────────────
  const inlineAttachments = useMemo<InlineAttachment[]>(
    () => Array.isArray((data as any)?.inlineAttachments) ? (data as any).inlineAttachments : [],
    [(data as any)?.inlineAttachments]
  );

  // ── Image Mode selector (t2i / i2i tabs) ──────────────────────
  const currentImageMode = (data as any)?.imageMode as string | undefined;
  const supportsInput = selectedModel?.supportsInput as string[] | undefined;
  const selectedModelTags = Array.isArray((selectedModel as any)?.tags) ? ((selectedModel as any).tags as string[]) : [];
  const isImageUpscaleModel = selectedModelTags.includes('image-upscale');
  const isRemoveBackgroundModel = selectedModelTags.includes('remove-background');
  const imageModeOptions = useMemo(() => {
    const si = Array.isArray(supportsInput) ? supportsInput : [];
    const supportsI2I = si.includes('i2i') || Boolean(selectedModel?.supportsEdit);
    const supportsT2I = si.includes('t2i');
    const opts: { value: string; label: string }[] = [];
    if (supportsT2I) opts.push({ value: 't2i', label: t('Text to Image') });
    if (supportsI2I) {
      const label = isImageUpscaleModel
        ? t('Image Super Resolution')
        : isRemoveBackgroundModel
          ? t('Remove background')
          : t('Image to Image');
      opts.push({ value: 'i2i', label });
    }
    return opts;
  }, [supportsInput, selectedModel?.supportsEdit, isImageUpscaleModel, isRemoveBackgroundModel, t]);

  useEffect(() => {
    const validModes = new Set(imageModeOptions.map((o) => o.value));
    if (validModes.size === 0) return;
    if (currentImageMode && validModes.has(currentImageMode)) return;
    const fallback = validModes.has('i2i') ? 'i2i' : [...validModes][0];
    if (fallback !== currentImageMode) {
      updateNodeData(id, { imageMode: fallback });
    }
  }, [id, updateNodeData, imageModeOptions, currentImageMode]);

  const handleImageModeChange = useCallback(
    (mode: string) => {
      updateNodeData(id, { imageMode: mode });
    },
    [id, updateNodeData]
  );

  const isI2IMode = imageModeOptions.length >= 2
    ? currentImageMode === 'i2i'
    : imageModeOptions.some((o) => o.value === 'i2i');
  const showImageModeSelector = imageModeOptions.length >= 2 || isImageUpscaleModel || isRemoveBackgroundModel;

  // 获取模型能力描述和提醒信息（基于 incomers，不再依赖全量 nodes/edges）
  // 传入 isI2IMode：文生图模式下不应提示连图/显示计数。
  const modelCapabilities = useImageGuidance({
    selectedModel,
    data,
    id,
    incomers: incomers as any,
    isI2IMode,
    t,
  });

  // Perf (Phase 9.2): default-value synchronization moved out of `toolbar`
  // useMemo into dedicated effects. Calling updateNodeData during render is a
  // React anti-pattern that triggers RF store writes mid-render and cascades
  // into extra commits.
  // 1) In I2I mode, when the model offers a 'match' size and current size is
  //    unset, default to 'match'.
  useEffect(() => {
    if (!isI2IMode) return;
    const sizeOptions = (modeCfg.sizes ?? []) as string[];
    if (!sizeOptions.includes('match' as any)) return;
    const currentSize = (data as any).size;
    const isSizeUnset =
      typeof currentSize === 'undefined' ||
      currentSize === null ||
      currentSize === '';
    if (isSizeUnset) {
      updateNodeData(id, { size: 'match' });
    }
  }, [isI2IMode, modeCfg, (data as any).size, id, updateNodeData]);
  // 2) Sync `image_resolution` to the first allowed value when the current
  //    value is missing/invalid for the current size + model resolution constraints.
  useEffect(() => {
    const allResolutionOptions = Array.isArray(modeCfg.resolutions)
      ? (modeCfg.resolutions as string[])
      : undefined;
    if (!allResolutionOptions || allResolutionOptions.length === 0) return;
    const resolutionConstraints = modeCfg.resolutionConstraints;
    const resolutionOptions = getEffectiveResolutions(
      allResolutionOptions,
      (data as any).size as string | undefined,
      resolutionConstraints,
    );
    if (!resolutionOptions || resolutionOptions.length === 0) return;
    const currentRes = (data as any)?.image_resolution as string | undefined;
    const effectiveRes =
      currentRes && resolutionOptions.includes(currentRes)
        ? currentRes
        : resolutionOptions[0];
    if (effectiveRes && effectiveRes !== currentRes) {
      updateNodeData(id, { image_resolution: effectiveRes });
    }
  }, [
    modeCfg,
    (data as any).size,
    (data as any).image_resolution,
    id,
    updateNodeData,
  ]);

  const effectiveInlineMedia = useMemo<InlineMediaConfig>(() => {
    if (!isI2IMode) return { allowInline: false };
    const modelCfg = modeCfg.inlineMedia;
    if (modelCfg) return modelCfg;
    if (selectedModel?.supportsEdit) {
      return {
        allowInline: true,
        image: { allow: true, maxCount: 9, maxSize: '10MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
        video: { allow: false, maxCount: 0, maxSize: '0' },
        audio: { allow: false, maxCount: 0, maxSize: '0' },
      };
    }
    return { allowInline: false };
  }, [selectedModel, modeCfg, isI2IMode]);

  const handleInlineAttachmentsChange = useCallback(
    (next: InlineAttachment[]) => {
      updateNodeData(id, { inlineAttachments: next });
    },
    [id, updateNodeData]
  );

  const inlineUploadRef = useRef<HTMLInputElement>(null);
  const [inlineUploading, setInlineUploading] = useState(false);
  const [subjectPickerOpen, setSubjectPickerOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const connectedImageCount = incomingImageInputs.length;

  const handleInlineUploadFiles = useCallback(async (files: FileList | null) => {
    if (!files?.length || !user?.id || !project?.id) return;
    setInlineUploading(true);
    try {
      const token = await getToken({ template: 'supabase' });
      if (!token) throw new Error('No auth token');
      const latest = (getNode(id) as any)?.data;
      const cur = Array.isArray(latest?.inlineAttachments) ? latest.inlineAttachments as InlineAttachment[] : [];
      const added: InlineAttachment[] = [];

      for (const file of Array.from(files)) {
        const fileType = detectMediaType(file.type);
        if (fileType !== 'image') {
          toast.error(t('Only image files are supported'));
          continue;
        }
        const typeCfg = effectiveInlineMedia.image;
        if (!typeCfg?.allow) {
          toast.error(t('This model does not support inline upload'));
          continue;
        }
        const totalCount = connectedImageCount + cur.length + added.length;
        if (effectiveInlineMedia.maxTotalCount != null && totalCount >= effectiveInlineMedia.maxTotalCount) {
          toast.error(t('Maximum {{count}} files allowed in total', { count: effectiveInlineMedia.maxTotalCount }));
          continue;
        }
        const typeCount = cur.filter(a => a.fileType === 'image').length + added.filter(a => a.fileType === 'image').length;
        if (typeCount >= (typeCfg.maxCount ?? Infinity)) {
          toast.error(t('Maximum {{count}} {{type}} allowed', { count: typeCfg.maxCount, type: t('image') }));
          continue;
        }
        if (typeCfg.formats?.length) {
          const mime = file.type.toLowerCase();
          if (!typeCfg.formats.some(f => f === mime || (f.endsWith('/*') && mime.startsWith(f.slice(0, -1))))) {
            toast.error(t('Unsupported format {{format}}', { format: file.type || file.name.split('.').pop() }));
            continue;
          }
        }
        if (file.size > parseBytes(typeCfg.maxSize)) {
          toast.error(t('File exceeds size limit of {{size}}', { size: typeCfg.maxSize }));
          continue;
        }
        const { url, type } = await uploadFile(file, 'files', {
          token, userId: user.id, projectId: String(project.id), assetType: 'image',
        });
        added.push({ id: nanoid(), url, type, fileType: 'image', name: file.name });
      }
      if (added.length > 0) {
        const freshData = (getNode(id) as any)?.data;
        const freshCur = Array.isArray(freshData?.inlineAttachments) ? freshData.inlineAttachments as InlineAttachment[] : [];
        updateNodeData(id, { inlineAttachments: [...freshCur, ...added].slice(0, 16) });
      }
    } catch (error) {
      handleError(t('Upload failed'), error);
    } finally {
      setInlineUploading(false);
      if (inlineUploadRef.current) inlineUploadRef.current.value = '';
    }
  }, [user?.id, project?.id, getToken, getNode, id, updateNodeData, t, effectiveInlineMedia, connectedImageCount]);

  const handlePickFromLibrary = useCallback((item: LibraryPickResult) => {
    const latest = (getNode(id) as any)?.data;
    const cur = Array.isArray(latest?.inlineAttachments) ? latest.inlineAttachments as InlineAttachment[] : [];
    const totalCount = connectedImageCount + cur.length;
    if (effectiveInlineMedia.maxTotalCount != null && totalCount >= effectiveInlineMedia.maxTotalCount) {
      toast.error(t('Maximum {{count}} files allowed in total', { count: effectiveInlineMedia.maxTotalCount }));
      return;
    }
    const typeCount = cur.filter(a => a.fileType === 'image').length;
    if (effectiveInlineMedia.image?.maxCount != null && typeCount >= effectiveInlineMedia.image.maxCount) {
      toast.error(t('Maximum {{count}} {{type}} allowed', { count: effectiveInlineMedia.image.maxCount, type: t('image') }));
      return;
    }
    const url = item.rawSrc ?? item.src;
    const att: InlineAttachment = {
      id: nanoid(),
      url,
      type: item.contentType || 'image/png',
      fileType: 'image',
      name: item.name,
    };
    updateNodeData(id, { inlineAttachments: [...cur, att].slice(0, 16) });
  }, [getNode, id, updateNodeData, t, effectiveInlineMedia, connectedImageCount]);

  const openLibraryPicker = useCallback(() => {
    void requestLibraryPick({ fileType: 'image' }).then((item) => {
      if (item) handlePickFromLibrary(item);
    });
  }, [handlePickFromLibrary]);

  // 从主体库选一个主体后，按其模式展开为内联图片：
  // - mosaic：1 张拼图作为 1 个 inline image
  // - multi：每张原图都加一条 inline image（按上限自动截断）
  const handlePickFromSubjectLibrary = useCallback((item: {
    id: string;
    name: string;
    image?: string | null;
    uploadedImage?: string[] | null;
    prompt?: string | null;
    mode?: 'mosaic' | 'multi';
  }) => {
    const latest = (getNode(id) as any)?.data;
    const cur = Array.isArray(latest?.inlineAttachments) ? latest.inlineAttachments as InlineAttachment[] : [];

    const mode = item.mode === 'multi' ? 'multi' : 'mosaic';
    const images: string[] = mode === 'multi' && Array.isArray(item.uploadedImage)
      ? (item.uploadedImage as string[]).map((u) => String(u || '').trim()).filter(Boolean)
      : [String(item.image || '').trim()].filter(Boolean);
    if (images.length === 0) return;

    // 先按上限算余量，再决定可加几张
    const totalCount = connectedImageCount + cur.length;
    const totalRoom = effectiveInlineMedia.maxTotalCount != null
      ? Math.max(0, effectiveInlineMedia.maxTotalCount - totalCount)
      : Infinity;
    const typeCount = cur.filter(a => a.fileType === 'image').length;
    const typeRoom = effectiveInlineMedia.image?.maxCount != null
      ? Math.max(0, effectiveInlineMedia.image.maxCount - typeCount)
      : Infinity;
    const room = Math.min(totalRoom, typeRoom);
    if (room <= 0) {
      const limit = Math.min(
        effectiveInlineMedia.maxTotalCount ?? Infinity,
        effectiveInlineMedia.image?.maxCount ?? Infinity,
      );
      if (Number.isFinite(limit)) {
        toast.error(t('Maximum {{count}} {{type}} allowed', { count: limit, type: t('image') }));
      }
      return;
    }

    const subjectName = String(item.name || '').trim() || t('Subject');
    const willAdd = images.slice(0, Number.isFinite(room) ? (room as number) : images.length);
    if (willAdd.length < images.length) {
      toast.error(t('Only added first {{count}} images due to limit', { count: willAdd.length }));
    }
    const added: InlineAttachment[] = willAdd.map((url, i) => ({
      id: nanoid(),
      url,
      type: 'image/png',
      fileType: 'image',
      name: images.length > 1 ? `${subjectName} ${i + 1}/${images.length}` : subjectName,
    }));
    updateNodeData(id, { inlineAttachments: [...cur, ...added].slice(0, 16) });
  }, [getNode, id, updateNodeData, t, effectiveInlineMedia, connectedImageCount]);

  // ── Drag-and-drop sensors ─────────────────────────────────────
  const inlineSortSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const imageGridOrder = (data as any)?.imageGridOrder as string[] | undefined;

  const orderedGridImages = useMemo((): MediaGridItem[] => {
    const map = new Map<string, MediaGridItem>();
    for (const img of incomingImageInputs) {
      map.set(img.rid, {
        id: img.rid, source: 'upstream',
        url: img.url, name: img.label, sourceId: img.sourceId,
      });
    }
    for (const att of inlineAttachments) {
      if (att.fileType !== 'image') continue;
      map.set(`in:${att.id}`, {
        id: `in:${att.id}`, source: 'inline',
        url: att.url, name: att.name, attId: att.id, previewUrl: att.previewUrl,
      });
    }
    const ordered: MediaGridItem[] = [];
    const seen = new Set<string>();
    if (Array.isArray(imageGridOrder)) {
      for (const rid of imageGridOrder) {
        const item = map.get(rid);
        if (item && !seen.has(rid)) { seen.add(rid); ordered.push(item); }
      }
    }
    for (const [rid, item] of map) {
      if (!seen.has(rid)) ordered.push(item);
    }
    return ordered;
  }, [incomingImageInputs, inlineAttachments, imageGridOrder]);

  const allImageInputsForMention = useMemo(() => {
    const result: typeof incomingImageInputs = [];
    let imageIdx = 0;
    // 反查 multi 主体信息（sourceId -> subTotal），用于在首图前插聚合项
    const subjectInfoBySource = new Map<string, { subTotal: number }>();
    for (const it of incomingImageInputs) {
      if (typeof it.subTotal === 'number' && it.subTotal > 1) {
        subjectInfoBySource.set(it.sourceId, { subTotal: it.subTotal });
      }
    }
    const subjectAggIndexBySource = new Map<string, number>();

    for (const item of orderedGridImages) {
      if (!item.url) continue;

      // multi 主体「第一次见到的子图」前插入聚合项（不依赖首图是否被跳过）
      if (item.source === 'upstream' && item.sourceId) {
        const info = subjectInfoBySource.get(item.sourceId);
        if (info && !subjectAggIndexBySource.has(item.sourceId)) {
          const baseLabel = String(item.name || '').replace(/\s+\d+\/\d+\s*$/, '').trim();
          const aggIdx = result.length;
          subjectAggIndexBySource.set(item.sourceId, aggIdx);
          result.push({
            idx: 0,
            sourceId: item.sourceId,
            rid: `up:${item.sourceId}:group`,
            subTotal: info.subTotal,
            kind: 'subjectGroup',
            groupChildIdxs: [],
            url: item.url,
            label: baseLabel || t('Image Group'),
            mediaType: 'image',
          });
        }
      }

      imageIdx++;
      const childResultIdx = result.length;
      result.push({
        idx: imageIdx,
        sourceId: item.source === 'upstream' ? item.sourceId! : `inline-${item.attId}`,
        rid: item.id,
        url: item.url,
        label: item.name || t('Inline image'),
      });

      if (item.source === 'upstream' && item.sourceId) {
        const aggIdx = subjectAggIndexBySource.get(item.sourceId);
        if (aggIdx !== undefined) {
          const agg = result[aggIdx];
          if (agg && Array.isArray(agg.groupChildIdxs)) agg.groupChildIdxs.push(childResultIdx);
        }
      }
    }
    return result;
  }, [orderedGridImages, incomingImageInputs, t]);

  const mentionLabels = useMemo<MentionLabels>(
    () => ({ image: t('Image'), video: t('Video'), audio: t('Audio') }),
    [t],
  );

  const commitInstructionsToStore = useCallback(
    (text: string) => updateNodeData(id, { instructions: text }),
    [id, updateNodeData],
  );

  const deferredRewrite = useDeferredInstructionsRewrite(
    localInstructions,
    setLocalInstructions,
    commitInstructionsToStore,
  );

  const handleGridImageReorder = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIds = orderedGridImages.map((i) => i.id);
      const oldIdx = oldIds.indexOf(String(active.id));
      const newIdx = oldIds.indexOf(String(over.id));
      if (oldIdx === -1 || newIdx === -1) return;
      const newIds = arrayMove(oldIds, oldIdx, newIdx);

      // 关键：先以最小工作量同步落地 imageGridOrder（与原实现一致），保证 dnd-kit 在
      // onDragEnd 时立刻看到新顺序，避免拖放时出现"甩一下再归位"。
      updateNodeData(id, { imageGridOrder: newIds });

      const baseline = deferredRewrite.getBaselineText();
      if (!baseline) return;
      // 同步算出 nextText（避免被异步阶段的 state 变化干扰）。image transform 只有 image 类型。
      const itemById = new Map(orderedGridImages.map((i) => [i.id, i]));
      const newInputs: Array<{ idx: number; rid: string; sourceId: string; label: string; mediaType: 'image' }> = [];
      let counter = 0;
      for (const rid of newIds) {
        const it = itemById.get(rid);
        if (!it || !it.url) continue;
        counter++;
        newInputs.push({ idx: counter, rid, sourceId: '', label: '', mediaType: 'image' });
      }
      const nextText = rewriteMentionsAfterReorder(baseline, allImageInputsForMention, newInputs as any, mentionLabels);
      if (nextText === baseline) return;
      // 推迟到下一帧再写入：当 prompt 含 mention 时，setLocalInstructions 会触发 mirror 层
      // 重渲染，与 dnd-kit 的 drop 过渡动画抢同一帧 → 视觉上"甩一下"。
      deferredRewrite.schedule(nextText);
    },
    [orderedGridImages, allImageInputsForMention, mentionLabels, id, updateNodeData, deferredRewrite]
  );

  const connectedCounts = useMemo(() => {
    return { image: incomingImageInputs.length, video: 0, audio: 0 };
  }, [incomingImageInputs.length]);

  /**
   * 删除内联素材或断开上游连线后，更新 instructions 中的 @[图像N] 引用：
   * 被删项对应的引用直接抹掉，更大的序号依次前移补位。
   *
   * 用 `getBaselineText()` 取基线（兼容刚 reorder 完 rAF 还未触发的中间态），
   * 用 `commitNow()` 同步落地（并取消任何尚未触发的 rAF，避免被它覆盖）。
   */
  const rewriteInstructionsForRemoval = useCallback(
    (shouldRemove: (it: { sourceId: string; idx: number; mediaType?: string; kind?: string }) => boolean) => {
      const baseline = deferredRewrite.getBaselineText();
      if (!baseline) return;
      const next = rewriteMentionsAfterRemoval(baseline, allImageInputsForMention, shouldRemove as any, mentionLabels);
      if (next !== baseline) deferredRewrite.commitNow(next);
    },
    [allImageInputsForMention, mentionLabels, deferredRewrite],
  );

  // Compute robust default size:
  // - If是图像到图像且模型支持 'match'，默认 'match'
  // - 否则选取第一个非 'match' 的像素尺寸
  const allSizes = modeCfg.sizes ?? [];
  const defaultPixelSize = useMemo(() => {
    return (allSizes.find((s: any) => s !== 'match') as string | undefined);
  }, [allSizes]);
  const presetPixelSize = useMemo(() => {
    try {
      const preset = getPresetAspectRatio();
      if (!preset) return undefined;
      const m = String(preset).trim().match(/^(\d+)\s*[:/]\s*(\d+)$/);
      if (!m) return undefined;
      const pw = Number(m[1]);
      const ph = Number(m[2]);
      if (!Number.isFinite(pw) || !Number.isFinite(ph) || pw <= 0 || ph <= 0) return undefined;

      for (const s of allSizes as any[]) {
        if (s === 'match') continue;
        const raw = String(s || '').trim();
        const mm = raw.match(/^(\d+)\s*x\s*(\d+)$/i);
        if (!mm) continue;
        const w = Number(mm[1]);
        const h = Number(mm[2]);
        if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) continue;
        // ratio match by cross-multiply
        if (w * ph === h * pw) return raw;
      }
    } catch {}
    return undefined;
  }, [allSizes]);

  const size =
    (data.size as any) ??
    (isI2IMode && (allSizes as any).includes('match')
      ? 'match'
      : (presetPixelSize ?? defaultPixelSize));
  // UI 展示尺寸：优先使用生成结果的真实尺寸（generated.size），避免“用户选择的 size”与“模型实际输出尺寸”不一致导致节点比例不更新
  const displaySize = useMemo(() => {
    try {
      const gsz = (data as any)?.generated?.size;
      if (typeof gsz === 'string' && /^\d+x\d+$/i.test(gsz)) return gsz;
    } catch {}
    return size;
  }, [size, (data as any)?.generated?.size]);
  // 缩略图骨架的默认宽高比：尽量用已有元数据（减少“加载前尺寸不对”的抖动）。
  // 真实尺寸统一存于 generated.size（"WxH" 字符串），这里解析它，再回退 displaySize / image_resolution。
  const defaultThumbAspectRatio = useMemo(() => {
    const parseWxH = (raw?: unknown) => {
      try {
        const s = String(raw || '').trim();
        const m = s.match(/^(\d+)\s*x\s*(\d+)$/i);
        if (!m) return undefined;
        const w = Number(m[1]);
        const h = Number(m[2]);
        if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return undefined;
        return `${w}/${h}`;
      } catch {
        return undefined;
      }
    };
    return (
      parseWxH((data as any)?.generated?.size) ||
      parseWxH(displaySize) ||
      parseWxH((data as any)?.image_resolution) ||
      undefined
    );
  }, [displaySize, (data as any)?.generated?.size, (data as any)?.image_resolution]);
  // 通用 UI 状态容器：根据模型动态渲染选项
  const modelUi = (data as any).modelUi ?? {};
  const uiOptions = useMemo<UiOption[]>(() => getUiOptionsForModel(modelId), [modelId]);
  const isToolbarBlocked = useMemo(() => {
    if (loading) return true;
    // 失败状态下即便残留了 generated.url（例如来自视频首帧），也不渲染该图片，
    // imgLoaded 永远不会变 true。此时必须解锁工具栏，否则用户无法重试/操作。
    if (isFailed) return false;
    return Boolean((effectiveGeneratedUrl && !imgLoaded) || isProcessing);
  }, [loading, effectiveGeneratedUrl, imgLoaded, isFailed, isProcessing]);

  // 进度逻辑由 useLinearProgress 承担

  // 订阅图片生成的 Realtime 状态，完成/失败时本地即时回填（生成专用 Realtime）
  useImageRealtime({
    projectId: project?.id,
    nodeId: id,
    data,
    modelId,
    updateNodeData,
    getNode,
    t,
  });

  useImagePolling({
    projectId: project?.id,
    nodeId: id,
    data,
    modelId,
    updateNodeData,
    getNode,
    t,
  });

  const runGenerate = useCallback(async (overrideData?: Record<string, any>) => {
    if (loading || !project?.id) return;
    setLoading(true);
    try {
      const ok = await createImageHandleGenerate({
        t,
        project,
        nodeId: id,
        getNodes,
        getEdges,
        data: overrideData ? { ...data, ...overrideData } : data,
        modelId,
        updateNodeData,
      })();
      if (ok) consumeModelPass(modelId, Number((data as any)?.imagesCount) || 1);
      setTimeout(() => mutate(project?.id ? `credits:${project.id}` : 'credits'), 3000);
    } catch {
      // inner handler already handles errors; this is a safety net
    } finally {
      setLoading(false);
    }
  }, [loading, project?.id, t, id, getNodes, getEdges, data, modelId, updateNodeData, consumeModelPass]);

  const handleGenerate = useCallback(async () => {
    if (loading || !project?.id) return;
    try {
      // precheck：当前模型要求的输入（提示词 / 图片）缺失时，弹窗提醒而不是直接报错。
      // 优先按当前模式取 precheckByMode，未配置时回退到顶层 precheck。
      const precheckList = getEffectiveImagePrecheck(selectedModel, currentImageMode);
      const isT2IWithI2I = currentImageMode === 't2i' && imageModeOptions.some((o) => o.value === 'i2i');
      // 上游图片只在「需要图片 precheck」或「t2i 忽略图提醒」时才需要，按需计算一次。
      const upstreamImages = (precheckList.includes('image') || isT2IWithI2I)
        ? (getAllImagesFromNodes(incomers as any, getEdges(), id) || [])
        : [];

      if (precheckList.includes('prompt')) {
        const inst = String(localInstructions || (data as any)?.instructions || '').trim();
        const nodePrompt = String((data as any)?.nodePrompt || '').trim();
        const hasPrompt =
          inst.length > 0 ||
          nodePrompt.length > 0 ||
          getTextFromTextNodes(incomers as any).length > 0 ||
          getPromptFromToolNodes(incomers as any).length > 0 ||
          getPromptFromUploadNodes(incomers as any).length > 0;
        if (!hasPrompt) {
          setPrecheckMissing('prompt');
          return;
        }
      }

      if (precheckList.includes('image')) {
        const hasInlineImage = inlineAttachments.some((a: any) => a?.fileType === 'image' && a?.url);
        if (upstreamImages.length === 0 && !hasInlineImage) {
          setPrecheckMissing('image');
          return;
        }
      }

      // 上游连了图片，但用户当前选的是文生图模式：图片会被忽略，弹窗提醒先确认走文生图还是图生图。
      if (isT2IWithI2I && upstreamImages.length > 0) {
        setT2iImageDialogOpen(true);
        return;
      }
    } catch {
      // 检测失败时不阻断正常生成
    }
    await runGenerate();
  }, [loading, project?.id, selectedModel, localInstructions, data, inlineAttachments, currentImageMode, imageModeOptions, incomers, getEdges, id, runGenerate]);

  const handleCancel = useCallback(() => {
    const gid = (getNode(id) as any)?.data?.processingGenerationId as string | undefined;
    updateNodeData(id, {
      status: undefined,
      processingGenerationId: undefined,
      runtimeEstimatedTime: undefined,
      error: undefined,
      updatedAt: new Date().toISOString(),
    });
    setLoading(false);
    toast(t('Generation cancelled'));
    if (gid) void cancelGeneration(gid);
  }, [id, getNode, updateNodeData, t]);

  // Auto-generate event hook (central router — one window listener for whole canvas)
  useAutoGenerateHandler(
    id,
    useCallback(() => {
      try {
        // 画布批量「运行全部」走自动生成，不弹交互式弹窗，直接按当前配置生成。
        void runGenerate();
      } catch {}
    }, [runGenerate]),
  );

  // Auto-cancel hook: lets the canvas-wide "Stop" button abort this node's
  // in-flight generation instead of letting it run to completion.
  useAutoCancelHandler(
    id,
    useCallback(() => {
      try {
        if (loading) handleCancel();
      } catch {}
    }, [loading, handleCancel]),
  );

  // 输入时只更新本地状态，不立即保存
  // Perf (Phase 9.5): useCallback so the function reference is stable across
  // renders — passed into the giant `toolbar` useMemo which would otherwise
  // invalidate on every keystroke (since setLocalInstructions itself triggers
  // a render → new function → memo busts).
  const handleInstructionsChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
    (event) => {
      setLocalInstructions(event.target.value);
    },
    [],
  );

  // 失去焦点时保存到节点数据
  const handleInstructionsBlur = useCallback(() => {
    setInstructionsFocused(false);
    // 只在值发生变化时才保存
    if (localInstructions !== (data.instructions ?? '')) {
      updateNodeData(id, { instructions: localInstructions });
    }
  }, [id, updateNodeData, localInstructions, data]);

  // 卸载时刷写未提交的输入到 store（关闭“渲染全部节点”后节点移出视口会被卸载且不触发 blur）。
  useFlushInstructionsOnUnmount(localInstructions, data.instructions ?? '', commitInstructionsToStore);

  const handleStyleChange = useCallback((styleValue: string, stylePrompt: string) => {
    setSelectedStyle(styleValue);
    
    // 使用本地状态而不是 data.instructions，因为用户可能正在编辑
    const currentInstructions = localInstructions || '';
    const previousStylePrompt = (data as any).stylePrompt || '';
    
    let updatedInstructions = currentInstructions;
    
    // Remove previous style prompt if it exists
    if (previousStylePrompt) {
      // Remove the previous style prompt and any leading/trailing commas and spaces
      updatedInstructions = updatedInstructions
        .replace(new RegExp(`${previousStylePrompt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(,\\s*|\\s*,)?`, 'g'), '')
        .replace(/^,\s*/, '') // Remove leading comma
        .replace(/,\s*$/, '') // Remove trailing comma
        .replace(/,\s*,/g, ',') // Remove double commas
        .trim();
    }
    
    // Add new style prompt if it's not 'none'
    if (stylePrompt && styleValue !== 'none') {
      updatedInstructions = updatedInstructions 
        ? `${updatedInstructions}, ${stylePrompt}`
        : stylePrompt;
    }
    
    // 同时更新本地状态和节点数据
    setLocalInstructions(updatedInstructions);
    updateNodeData(id, { 
      style: styleValue, 
      stylePrompt: styleValue !== 'none' ? stylePrompt : '',
      instructions: updatedInstructions 
    });
  }, [id, updateNodeData, localInstructions, data]);

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.max(36, textareaRef.current.scrollHeight) + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localInstructions]);

  // Image 节点的 size 兜底（对齐 video 的“空/非法->写默认”策略），并插入“预设比例”：
  // - i2i 且模型支持 'match'：当 size 为空/非法时，默认写 'match'
  // - 否则：当 size 为空/非法/或在 t2i 下为 'match' 时，优先写入预设比例对应的 WxH（若模型 sizes 中存在），否则回退到模型第一个像素尺寸
  useEffect(() => {
    try {
      // imageMode 尚未确定时不做 size 校验，避免与 imageMode 初始化 effect 竞争
      if (!currentImageMode && imageModeOptions.length >= 2) return;

      const sizes = (modeCfg.sizes ?? []) as any[];
      if (!Array.isArray(sizes) || sizes.length === 0) return;
      const hasMatch = sizes.includes('match' as any);
      const firstPixel = sizes.find((s: any) => s !== 'match') as string | undefined;
      const currentSize = (data as any).size as string | undefined | null;
      const isHistoryPreview = Boolean((data as any).activeHistoryId);
      const isUnset =
        typeof currentSize === 'undefined' || currentSize === null || currentSize === '';
      const isInvalid =
        !isUnset && currentSize !== 'Auto' && !sizes.includes(String(currentSize) as any);
      const resetGenerated = isHistoryPreview
        ? {}
        : { generated: undefined, activeHistoryId: undefined };

      if (isI2IMode && hasMatch) {
        if (isUnset || isInvalid) {
          if (String(currentSize) !== 'match') {
            updateNodeData(id, {
              size: 'match',
              ...resetGenerated,
            });
          }
        }
        return;
      }

      // t2i (or i2i without match): match is not a valid default
      const needsFix = isUnset || isInvalid || String(currentSize) === 'match';
      if (!needsFix) return;

      const pick = (presetPixelSize ?? firstPixel) as string | undefined;
      if (!pick) return;
      if (String(currentSize) !== String(pick)) {
        updateNodeData(id, {
          size: pick,
          ...resetGenerated,
        });
      }
    } catch {}
  }, [
    isI2IMode,
    currentImageMode,
    imageModeOptions,
    modeCfg,
    presetPixelSize,
    id,
    updateNodeData,
    data,
  ]);

  const handleSwitchToHistory = useCallback((historyItem: any) => {
    const nextUrl = historyItem?.url || (Array.isArray(historyItem?.urls) ? historyItem.urls[0] : undefined);
    if (!nextUrl) return;
    const nextUrls = Array.isArray(historyItem?.urls) ? historyItem.urls.filter(Boolean) : undefined;
    const nextInstructions = historyItem?.prompt ?? '';
    const histModelId = typeof historyItem?.modelId === 'string' ? historyItem.modelId : undefined;
    const canSwitchModel = Boolean(histModelId && (imageModels as any)?.[histModelId]);
    const targetModel = canSwitchModel
      ? (imageModels as any)[histModelId!]
      : selectedModel;
    const { nodeSize, generatedSize } = resolveImageHistorySize(
      historyItem?.size,
      (getEffectiveImageConfig(targetModel, currentImageMode).sizes ?? []) as string[],
      isI2IMode,
    );
    const generated: Record<string, unknown> = nextUrls && nextUrls.length > 0
      ? { url: nextUrl, urls: nextUrls, type: historyItem.type }
      : { url: nextUrl, type: historyItem.type };
    // 历史条目里已带真实像素时，连同 sizeUrl 一起写入，使切换“权威化”：
    // 显示尺寸直接取自该条目，无需进入视口后再解析文件头重测。
    if (generatedSize) {
      generated.size = generatedSize;
      generated.sizeUrl = nextUrl;
    }
    // 同时更新本地状态和节点数据
    setLocalInstructions(nextInstructions);
    updateNodeData(id, {
      generated,
      activeHistoryId: historyItem.id,
      description: historyItem.description,
      instructions: nextInstructions,
      ...(nodeSize ? { size: nodeSize } : {}),
      status: 'completed',
      error: undefined,
      ...(canSwitchModel ? { model: histModelId } : {}),
    });
  }, [id, updateNodeData, selectedModel, isI2IMode, currentImageMode]);

  const toolbar = useMemo<ComponentProps<typeof NodeLayout>['toolbar']>(() => {
    // Flora style: create a unified toolbar container
    const accessFiltered = filterModelsClient(imageModels, modelAccessMap, userPlan, userInterval, userTags, modelPassMap, planLevels);
    // i2i 模式下，不支持图生图的模型不再隐藏，而是保留并打上标记，
    // 由 ModelSelector 显示提示（保持与视频选择器一致的“可见但提醒”体验）。
    const filteredImageModels = isI2IMode
      ? markIncompatibleModels(accessFiltered, 'i2i')
      : accessFiltered;

    // Three-row toolbar:
    // - Row 2: 3 equal columns (model / size / resolution) with fixed layout
    // - Row 3: actions
    const actionControls: any[] = [];
    const push = (el: any) => actionControls.push(el);
    const COL_W: number | string | 'auto' = '100%';

    // Model selector (shared)
    const modelControl = (
      <ModelItem
        id={id}
        value={modelId}
        options={filteredImageModels}
        width={COL_W}
        onChange={(value) => {
          rememberLastSelectedModelId('image', value);
          const newModel = imageModels[value];
          // 新模型按当前模式取有效配置（configByMode 覆盖顶层）
          const newModeCfg = getEffectiveImageConfig(newModel, currentImageMode ?? (isI2IMode ? 'i2i' : 't2i'));
          const newSizes = newModeCfg.sizes ?? [];
          // 切换模型视为“重置配置”：清除旧的 size/resolution/modelUi 等，按新模型重新初始化
          const nextData: any = {
            model: value,
            // 清除当前展示结果，避免旧模型产物与新模型配置混用
            generated: undefined,
            // 清除高级选项（不同模型的 uiOptions key 往往不同）
            modelUi: {},
            // 取消选中的历史记录（历史数据仍保留）
            activeHistoryId: undefined,
            // size：默认先清空，避免新模型无 sizes 时遗留旧值
            size: undefined,
          };

          // size：
          // - i2i 且支持 match：默认 match
          // - 否则：优先使用“预设比例”所对应的尺寸（若新模型 sizes 中存在），否则回退到第一个像素尺寸
          if (isI2IMode && newSizes.includes('match' as any)) {
            nextData.size = 'match';
          } else {
            const presetSizeFromNewModel = (() => {
              try {
                const preset = getPresetAspectRatio();
                if (!preset) return undefined;
                const m = String(preset).trim().match(/^(\d+)\s*[:/]\s*(\d+)$/);
                if (!m) return undefined;
                const pw = Number(m[1]);
                const ph = Number(m[2]);
                if (!Number.isFinite(pw) || !Number.isFinite(ph) || pw <= 0 || ph <= 0)
                  return undefined;
                for (const s of newSizes as any[]) {
                  if (s === 'match') continue;
                  const raw = String(s || '').trim();
                  const mm = raw.match(/^(\d+)\s*x\s*(\d+)$/i);
                  if (!mm) continue;
                  const w = Number(mm[1]);
                  const h = Number(mm[2]);
                  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) continue;
                  if (w * ph === h * pw) return raw;
                }
              } catch {}
              return undefined;
            })();

            const firstPixel = newSizes.find((s) => s !== 'match');
            const pick = (presetSizeFromNewModel ?? firstPixel) as string | undefined;
            if (pick) nextData.size = pick;
          }

          // resolution：若新模型支持 resolutions，则默认第一项；否则清空
          const resOptions = Array.isArray(newModeCfg.resolutions)
            ? ((newModeCfg.resolutions as string[]) ?? [])
            : [];
          nextData.image_resolution = resOptions.length ? resOptions[0] : undefined;

          // imagesCount：若新模型允许多张，重置为默认第一项（否则清空）
          const cnt = Array.isArray(newModeCfg.imagesCountOptions)
            ? newModeCfg.imagesCountOptions[0]
            : undefined;
          nextData.imagesCount = typeof cnt === 'number' ? cnt : undefined;

          updateNodeData(id, nextData);
        }}
        disabled={isToolbarBlocked}
      />
    );

    // Size selector (always render; disable if unavailable)
    const sizeOptions = modeCfg.sizes ?? [];
    const visibleSizeOptions = isI2IMode
      ? (sizeOptions as string[])
      : (sizeOptions.filter((o) => o !== 'match') as string[]);

    // (Phase 9.2) Default-value sync moved to dedicated useEffect above —
    // never write to RF store from render-phase useMemo.

    // 来自模型定义的尺寸备注（可覆盖默认映射）
    const notesFromModel = modeCfg.sizeNotes as Record<
      string,
      string
    > | undefined;
    const notes = notesFromModel ?? undefined;

    const sizeControl = (
      <SizeItem
        id={id}
        value={String(
          visibleSizeOptions.length
            ? (size || (visibleSizeOptions[0] ?? '512x512'))
            : 'Auto'
        )}
        options={visibleSizeOptions.length ? visibleSizeOptions : ['Auto']}
        notes={notes}
        width={COL_W}
        onChange={(value) => {
          // 与视频节点一致：基础参数变更视为“重置配置”
          // - 清除旧生成结果，避免参数与产物不一致
          // - 退出历史预览（activeHistoryId）
          if (value === 'Auto' || value === '') {
            updateNodeData(id, {
              size: undefined,
              generated: undefined,
              activeHistoryId: undefined,
            });
            return;
          }
          updateNodeData(id, {
            size: value,
            generated: undefined,
            activeHistoryId: undefined,
          });
        }}
        disabled={isToolbarBlocked || visibleSizeOptions.length === 0}
      />
    );

    // Resolution selector (always render; disable if unavailable)
    const allResolutionOptions = Array.isArray(modeCfg.resolutions)
      ? (modeCfg.resolutions as string[])
      : undefined;
    const resolutionConstraints = modeCfg.resolutionConstraints;
    const resolutionOptions = allResolutionOptions
      ? getEffectiveResolutions(allResolutionOptions, size as string | undefined, resolutionConstraints)
      : undefined;
    // 默认值：首次未设置时写入模型的第一个分辨率；若模型未提供分辨率列表，则展示 Auto（不写入数据）
    const currentRes = (data as any)?.image_resolution as string | undefined;
    const effectiveRes = resolutionOptions && resolutionOptions.length > 0
      ? (currentRes && resolutionOptions.includes(currentRes) ? currentRes : resolutionOptions[0])
      : undefined;
    // (Phase 9.2) Default-value sync moved to dedicated useEffect above.

    const resolutionControl = (
      <ImageResolutionSelector
        id={id}
        value={(effectiveRes ?? 'Auto') as any}
        options={(resolutionOptions && resolutionOptions.length ? resolutionOptions : ['Auto']) as any}
        width={COL_W}
        onChange={(value) => {
          // 与视频节点一致：基础参数变更视为“重置配置”
          if (value === 'Auto' || value === '') {
            updateNodeData(id, {
              image_resolution: undefined,
              generated: undefined,
              activeHistoryId: undefined,
            });
            return;
          }
          updateNodeData(id, {
            image_resolution: value,
            generated: undefined,
            activeHistoryId: undefined,
          });
        }}
        disabled={isToolbarBlocked || !resolutionOptions || resolutionOptions.length === 0}
      />
    );

    const topControls = (
      <div
        className="grid gap-1.5 w-full"
        style={{ gridTemplateColumns: '6fr 3fr 2fr' }}
      >
        <div className="min-w-0">{modelControl}</div>
        <div className="min-w-0">{sizeControl}</div>
        <div className="min-w-0">{resolutionControl}</div>
      </div>
    );

    const inlineUiOptions = uiOptions.filter((o) => o.group === 'inline');
    const panelUiOptions = uiOptions.filter((o) => o.group !== 'inline');

    const onInlineOptionChange = (key: string, val: unknown) => {
      updateNodeData(id, { modelUi: { ...(modelUi || {}), [key]: val } });
    };

    const inlineElements = inlineUiOptions.map((opt) => {
      if (opt.type === 'switch') {
        const val = typeof modelUi?.[opt.key] === 'boolean' ? modelUi[opt.key] as boolean : opt.default;
        return (
          <label
            key={opt.key}
            htmlFor={`${id}-${opt.key}`}
            className="inline-flex h-8 shrink-0 cursor-pointer items-center gap-2"
          >
            <span className="inline-flex min-w-0 items-center gap-1 leading-none">
              <span className="truncate">{t(opt.label)}</span>
              {opt.remark ? <OptionRemark text={t(opt.remark)} /> : null}
            </span>
            <Switch
              id={`${id}-${opt.key}`}
              checked={val}
              onCheckedChange={(v) => onInlineOptionChange(opt.key, v)}
              disabled={isToolbarBlocked}
            />
          </label>
        );
      }
      if (opt.type === 'select') {
        const val = typeof modelUi?.[opt.key] === 'string' ? modelUi[opt.key] as string : opt.default;
        return (
          <div key={opt.key} className="min-w-0">
            <InlineComboboxSelect
              id={`${id}-${opt.key}`}
              label={t(opt.label)}
              value={val}
              options={opt.options}
              onChange={(v) => onInlineOptionChange(opt.key, v)}
              disabled={isToolbarBlocked}
            />
          </div>
        );
      }
      return null;
    }).filter(Boolean);

    const middleControls = inlineElements.length ? (
      <InlineOptionsRow>{inlineElements}</InlineOptionsRow>
    ) : undefined;

    // Split to action row
    // (actions start here)

    // Generate button with count selector (combined) — render at far right
    const generateControl = (
      <GenerateItem
        key="generate"
        loading={loading || isProcessing}
        generatedUrl={data.generated?.url}
        onGenerate={handleGenerate}
        onCancel={isProcessing ? handleCancel : undefined}
        disabled={loading || !project?.id || isToolbarBlocked}
        estimatedCredits={estimatedCredits}
        passRemaining={passRemaining}
        discountMultiplier={discountMultiplier}
        passDiscountMultiplier={passDiscountMultiplier}
        passCount={passCount}
        imagesCountOptions={imagesCountOptions.length > 1 ? imagesCountOptions : undefined}
        currentCount={currentImagesCount}
        onCountChange={(count) => {
          if (!isToolbarBlocked) {
            // 与视频节点一致：基础参数变更视为“重置配置”
            updateNodeData(id, {
              imagesCount: count,
              generated: undefined,
              activeHistoryId: undefined,
            });
          }
        }}
      />
    );

    // Download / Split buttons (always render; disable when not available)
    const downloadData = (data.generated as any) as
      | { url?: string; urls?: string[]; type?: string }
      | undefined;
    const hasGenerated = Boolean(downloadData?.url || (Array.isArray(downloadData?.urls) && downloadData?.urls?.length));
    const hasMultiple =
      Boolean(downloadData) &&
      'urls' in (downloadData as any) &&
      Array.isArray((downloadData as any).urls) &&
      (downloadData as any).urls.length > 1;
    // 确定当前显示的图片URL：优先使用 data.generated.url（用户切换后的当前图片），否则使用第一张
    const currentUrl =
      downloadData?.url ||
      (Array.isArray(downloadData?.urls) && downloadData?.urls.length > 0 ? downloadData.urls[0] : undefined);
    // 确定当前图片在多张图片中的索引位置
    const currentIndex =
      hasMultiple && Array.isArray(downloadData?.urls) && currentUrl ? downloadData.urls.indexOf(currentUrl) : undefined;
    const totalCount = hasMultiple && Array.isArray(downloadData?.urls) ? downloadData.urls.length : undefined;

    push(
      <DownloadItem
        key="download"
        onDownload={async () => {
          if (!hasGenerated || !downloadData) return;
          if (!currentUrl) return;
          const resolvedUrl = resolveDownloadableUrl(currentUrl, assetPreviewUrl);
          if (!resolvedUrl) return;
          if (hasMultiple && currentIndex !== undefined && currentIndex >= 0 && totalCount !== undefined) {
            download({ url: resolvedUrl, type: downloadData.type }, id, 'png', currentIndex, totalCount);
          } else {
            download({ url: resolvedUrl, type: downloadData.type }, id, 'png');
          }
        }}
        onDownloadThumbnail={async () => {
          if (!hasGenerated || !downloadData) return;
          if (!currentUrl) return;
          const resolvedUrl = resolveDownloadableUrl(currentUrl, assetPreviewUrl);
          if (!resolvedUrl) return;
          // 缩略图转为 PNG 的 blob 地址（不带扩展名），需显式指定类型
          const thumbUrl = await resolveThumbnailDownloadUrl(resolvedUrl);
          if (hasMultiple && currentIndex !== undefined && currentIndex >= 0 && totalCount !== undefined) {
            download({ url: thumbUrl, type: 'image/png' }, id, 'png', currentIndex, totalCount);
          } else {
            download({ url: thumbUrl, type: 'image/png' }, id, 'png');
          }
        }}
        onDownloadAll={
          hasMultiple && Array.isArray(downloadData?.urls) && downloadData.urls.length > 0
            ? () => {
                download({ urls: downloadData.urls as string[], type: downloadData.type }, id, 'png');
              }
            : undefined
        }
        onDownloadAllThumbnail={
          hasMultiple && Array.isArray(downloadData?.urls) && downloadData.urls.length > 0
            ? async () => {
                const thumbUrls = await resolveThumbnailDownloadUrls(downloadData.urls as string[]);
                download({ urls: thumbUrls, type: 'image/png' }, id, 'png');
              }
            : undefined
        }
        downloadData={downloadData}
        disabled={!hasGenerated || !imgLoaded || isToolbarBlocked}
      />
    );

    push(
      <Button
        key="split"
        variant="ghost"
        size="sm"
        className="rounded-md h-8 px-2 text-xs"
        onClick={() => {
          try {
            if (isPreview) return;
            if (!hasMultiple || !downloadData || !Array.isArray(downloadData.urls) || downloadData.urls.length <= 1) return;
            const cur = getNode(id) as any;
            if (!cur) return;
            const baseX = cur?.position?.x ?? 0;
            const baseY = cur?.position?.y ?? 0;
            const width = (cur?.measured?.width ?? 300) as number;
            const height = (cur?.measured?.height ?? 240) as number;
            // 拆分模式：与原节点保持横向间距，但新节点纵向紧贴
            const gap = 120;
            const verticalGap = 0;

            const urls = downloadData.urls as string[];
            const imageType = downloadData.type || 'image/png';

            // 为每张图像创建新节点
            const newNodes: string[] = [];
            urls.forEach((url, index) => {
              const position = {
                x: baseX + width + gap,
                y: baseY + index * (height + verticalGap),
              };

              const newId = addNode('image', {
                position,
                selected: index === 0, // 只选中第一个新节点
                data: {
                  viewMode: 'transform',
                  model: modelId,
                  size: (data as any)?.size,
                  instructions: data.instructions ?? '',
                  generated: {
                    url,
                    type: imageType,
                  },
                  description: (data as any)?.description,
                  updatedAt: new Date().toISOString(),
                },
              });

              if (newId) {
                newNodes.push(newId);
              }
            });

            // 为新节点创建连接（从当前节点连接到新节点）
            requestAnimationFrame(() => {
              newNodes.forEach((newId) => {
                try {
                  addEdges({
                    id: nanoid(),
                    source: id,
                    target: newId,
                    type: 'animated',
                  } as any);
                } catch {}
              });

              // 选中第一个新节点
              if (newNodes.length > 0) {
                try {
                  setNodes((prev: any[]) =>
                    prev.map((n: any) => ({
                      ...n,
                      selected:
                        newNodes.includes(String(n.id)) &&
                        String(n.id) === String(newNodes[0]),
                    }))
                  );
                } catch {}
              }
            });

            toast.success(t('Split into {{count}} nodes', { count: urls.length }));
          } catch (error) {
            handleError(t('Failed to split images'), error);
          }
        }}
        disabled={isPreview || !hasMultiple || isToolbarBlocked}
      >
        <Scissors size={12} className="mr-1" />
        {t('Split')}
      </Button>
    );

    // History button (always render; disable when not available)
    {
      const historyData = (data as any).history;
      push(
        <HistoryItem
          key="history"
          items={historyData}
          activeId={(data as any).activeHistoryId}
          onSelect={(rec: any) => handleSwitchToHistory(rec)}
          disabled={isToolbarBlocked || !Array.isArray(historyData) || historyData.length === 0}
        />
      );
    }

    // Plugins + Advanced should be in the same group as Download/Split/History (left side).
    push(
      <ImagePluginButton
        key="plugins"
        plugins={(selectedModel as any)?.plugin}
        enabledPlugins={(data as any)?.pluginEnabled}
        onEnabledPluginsChange={(next) => updateNodeData(id, { pluginEnabled: next })}
        cameraValue={(data as any)?.cameraControl}
        onCameraApply={(payload) => {
          updateNodeData(id, {
            nodePrompt: payload.nodePrompt,
            cameraControl: payload.selection,
          });
        }}
        onCameraClose={() => {
          updateNodeData(id, { nodePrompt: undefined });
        }}
        disabled={isToolbarBlocked}
      />
    );

    const advancedDisabled = isToolbarBlocked || panelUiOptions.length === 0;
    const advancedExpanded = advancedOpen && !advancedDisabled;
    push(
      <AdvancedToggleButton
        key="advanced"
        expanded={advancedExpanded}
        disabled={advancedDisabled}
        onToggle={() => setAdvancedOpen((v) => !v)}
      />
    );

    // Put generate button at the far right of the action row
    push(
      <div key="generate-right" className="ml-auto flex items-center">
        {generateControl}
      </div>
    );

    const hideModelInstructions = Boolean((selectedModel as any)?.hideInstructions);
    const showInlineInputs = isI2IMode && Boolean(effectiveInlineMedia.allowInline && effectiveInlineMedia.image?.allow);

    // Return Flora-style connected toolbar layout
    return [{
      children: (
        <ThreeRowToolbar
          showInstructions={!hideModelInstructions || showInlineInputs}
          fixedWidthPx={720}
          instructions={(
            <div className="flex w-full flex-col gap-2 px-2 py-2">
              {/* ── Image mode selector (t2i / i2i tabs) ── */}
              {showImageModeSelector && (
                <div className="flex items-center gap-1.5 px-0.5">
                  {imageModeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={isToolbarBlocked}
                      onClick={() => handleImageModeChange(opt.value)}
                      className={cn(
                        "nodrag h-6 px-2.5 rounded-full text-[11px] font-medium transition-colors border",
                        currentImageMode === opt.value
                          ? "bg-primary/10 text-primary border-primary/30"
                          : "text-muted-foreground/40 border-transparent hover:text-muted-foreground/70 hover:bg-muted/30",
                        "disabled:opacity-30 disabled:pointer-events-none"
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                  {isI2IMode && (
                    <span className="flex-1 text-center text-[11px] text-muted-foreground/30 select-none">{t('Drag to reorder')}</span>
                  )}
                  <button
                    type="button"
                    className="nodrag ml-auto shrink-0 flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-primary/70 hover:text-primary transition-colors"
                    onClick={() => instructionInputRef.current?.openEditor()}
                  >
                    <Maximize2 size={12} />
                    {t('Expand Prompt Editor')}
                  </button>
                </div>
              )}
              {/* ── Draggable image grid (upstream + inline, i2i only) ── */}
              {isI2IMode && (orderedGridImages.length > 0 || (effectiveInlineMedia.allowInline && effectiveInlineMedia.image?.allow)) ? (
                <DraggableMediaGrid
                  items={orderedGridImages}
                  sensors={inlineSortSensors}
                  onReorder={handleGridImageReorder}
                  getLabel={(item, idx) => {
                    const base = `${t('Image')} ${idx + 1}`;
                    return item.name ? `${base}（${item.name}）` : base;
                  }}
                  onRemoveInline={(attId) => {
                    rewriteInstructionsForRemoval((it) => it.sourceId === `inline-${attId}`);
                    handleInlineAttachmentsChange(inlineAttachments.filter((a) => a.id !== attId));
                  }}
                  onDisconnectUpstream={(sourceId) => {
                    rewriteInstructionsForRemoval((it) => it.sourceId === sourceId);
                    setEdges((eds) => eds.filter((e) => !(e.source === sourceId && e.target === id)));
                  }}
                  disabled={isToolbarBlocked}
                  canUpload={canAddMore(effectiveInlineMedia, inlineAttachments, connectedCounts)}
                  isUploading={inlineUploading}
                  onUpload={() => inlineUploadRef.current?.click()}
                  uploadTitle={t("Upload image")}
                  uploadPlaceholderLabel={t('Waiting for upload')}
                  onPickFromLibrary={openLibraryPicker}
                  pickFromLibraryTitle={t("Pick from library")}
                  onPickFromSubjectLibrary={() => setSubjectPickerOpen(true)}
                  pickFromSubjectLibraryTitle={t("Pick from subject library")}
                  renderContent={(item) => {
                    if (item.url && item.url.startsWith('Asset://') && !item.previewUrl) {
                      return (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-0.5 bg-primary/5">
                          <Package size={14} className="text-primary/60" />
                        </div>
                      );
                    }
                    const displayUrl = item.previewUrl ?? item.url;
                    if (displayUrl) {
                      const thumbTransform = { width: 96, height: 96, resize: 'cover', quality: 70 } as const;
                      return (
                        <VariantImage
                          url={displayUrl}
                          transform={thumbTransform}
                          width={96}
                          height={96}
                          className="h-full w-full object-cover"
                        />
                      );
                    }
                    return (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-0.5">
                        <Sparkles size={12} className="text-muted-foreground/40" />
                        <span className="text-[7px] leading-tight text-muted-foreground/40">{t('Pending')}</span>
                      </div>
                    );
                  }}
                  renderOverlay={(item) => (
                    item.source === 'upstream' && item.url ? (
                      <button type="button" className="absolute inset-0 cursor-pointer opacity-0" onClick={() => focusNodeById(item.sourceId!)} />
                    ) : null
                  )}
                />
              ) : null}
              {isI2IMode && orderedGridImages.some(i => i.url) ? (
                <div className="h-px bg-border/80 -mx-2" />
              ) : null}
              <input
                ref={inlineUploadRef}
                type="file"
                accept={buildAcceptFromConfig(effectiveInlineMedia) || 'image/png,image/jpeg,image/webp'}
                multiple
                className="hidden"
                onChange={(e) => void handleInlineUploadFiles(e.target.files)}
              />
              {!hideModelInstructions ? (
                <div className="relative w-full min-w-0">
                  <InstructionInput
                    ref={instructionInputRef}
                    textareaRef={textareaRef}
                    value={localInstructions}
                    onChange={handleInstructionsChange}
                    onInput={adjustTextareaHeight}
                    onFocus={() => setInstructionsFocused(true)}
                    onBlur={handleInstructionsBlur}
                    onCommit={(v) => {
                      setLocalInstructions(v);
                      updateNodeData(id, { instructions: v });
                    }}
                    showPlaceholder={!localInstructions.length}
                    imageInputs={isI2IMode ? allImageInputsForMention : []}
                    placeholder={isI2IMode ? t('Type @ to reference media, / to summon prompts') : t('Type / to summon prompts')}
                    hideExpandButton={showImageModeSelector}
                  />
                </div>
              ) : null}
            </div>
          )}
          topControls={topControls}
          middleControls={middleControls}
          bottomControls={actionControls}
          expandedPanel={
            advancedExpanded ? (
              <AdvancedInlinePanel
                id={id}
                uiOptions={panelUiOptions as any}
                modelUi={modelUi as any}
                canUseAdvanced={canUseAdvanced}
                onChange={(key: string, val: unknown) =>
                  updateNodeData(id, { modelUi: { ...(modelUi || {}), [key]: val } })
                }
              />
            ) : undefined
          }
        />
      ),
    }];
  }, [
    id,
    updateNodeData,
    loading,
    data,
    handleGenerate,
    handleCancel,
    modeCfg,
    size,
    isI2IMode,
    project?.id,
    modelId,
    handleSwitchToHistory,
    handleInstructionsChange,
    handleInstructionsBlur,
    adjustTextareaHeight,
    localInstructions,
    isToolbarBlocked,
    uiOptions,
    modelUi,
    canUseAdvanced,
    advancedOpen,
    getNode,
    addNode,
    addEdges,
    setNodes,
    imgLoaded,
    t,
    inlineAttachments,
    handleInlineAttachmentsChange,
    inlineUploading,
    handleInlineUploadFiles,
    effectiveInlineMedia,
    allImageInputsForMention,
    orderedGridImages,
    handleGridImageReorder,
    inlineSortSensors,
    connectedCounts,
    imageModeOptions,
    currentImageMode,
    showImageModeSelector,
    handleImageModeChange,
    modelAccessMap,
    modelPassMap,
    userPlan,
    userInterval,
    userTags,
    planLevels,
    estimatedCredits,
    passRemaining,
    discountMultiplier,
    passDiscountMultiplier,
    passCount,
    imagesCountOptions,
    currentImagesCount,
  ]);

  const aspectRatio = useMemo(() => {
    if (!displaySize || displaySize === 'match' || displaySize === 'Auto') {
      return '1/1';
    }

    // 防御：若 displaySize 不是 WxH，避免显示 NaN/NaN
    const [width, height] = String(displaySize).split('x').map(Number);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return '1/1';
    }
    return `${width}/${height}`;
  }, [displaySize]);

  useEffect(() => {
    setImgLoaded(false);
  }, [effectiveGeneratedUrl, (data as any)?.generated?.urls]);

  // Listen to image updates from ImageTopToolbar editor
  useEffect(() => {
    const onUpdated = (e: any) => {
      try {
        if (e?.detail?.id !== id) return;
        const url = e?.detail?.url as string | undefined;
        const type = e?.detail?.type as string | undefined;
        const width = Number(e?.detail?.width);
        const height = Number(e?.detail?.height);
        const sizeStr = e?.detail?.size as string | undefined;
        if (!url) return;
        const latestNode = (() => { try { return getNode(id) as any; } catch { return undefined; } })();
        const prevG = (latestNode?.data?.generated ?? {}) as any;
        const nextG: any = { ...prevG, url, type: type || 'image/png' };
        const sz =
          (typeof sizeStr === 'string' && /^\d+x\d+$/i.test(sizeStr) ? sizeStr : undefined) ||
          (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0 ? `${width}x${height}` : undefined);
        if (sz) {
          // 真实尺寸统一存于 generated.size（"WxH"）+ sizeUrl。
          nextG.size = sz;
          nextG.sizeUrl = url;
        }
        updateNodeData(id, {
          generated: nextG,
          content: { url, type: type || 'image/png' },
          sourceUrl: url,
          updatedAt: new Date().toISOString(),
        });
      } catch {}
    };
    window.addEventListener('image_node_updated', onUpdated);
    return () => window.removeEventListener('image_node_updated', onUpdated);
  }, [id, getNode, updateNodeData]);

  const bgStyle = { backgroundColor: 'var(--node-bg)' };
  // Perf (Phase 9.7): merge selected + measuredHeight into a single store
  // subscription with shallow equality. N image nodes × 2 zustand listeners
  // → N × 1 listener; both fields almost always change together (selection
  // → toolbar reflow → measure) so a single subscription is no worse and
  // halves listener overhead.
  const nodeSliceSelector = useMemo(
    () => (s: any) => {
      const n = s?.nodeLookup?.get?.(String(id));
      return {
        selected: Boolean(n?.selected),
        measuredHeight: n?.measured?.height,
        measuredWidth: n?.measured?.width,
      };
    },
    [id],
  );
  const { selected: isSelected, measuredHeight, measuredWidth } = useStore(
    nodeSliceSelector,
    shallow,
  );
  const isMultiSelect = useIsMultiSelect();
  const isCollapsed = Boolean((data as any)?.collapsed);
  const insetClassBase = 'inset-0';
  const mainUrl = useMemo(() => {
    if (effectiveGeneratedUrl) return effectiveGeneratedUrl;
    if (Array.isArray(generatedUrls) && generatedUrls.length > 0) return generatedUrls[0];
    return undefined;
  }, [effectiveGeneratedUrl, generatedUrls]);

  const imgOutputRef = useRef<HTMLDivElement>(null);
  const inViewport = useInViewport(imgOutputRef, mainUrl);
  const shouldRenderImage = inViewport || isSelected;

  // 兜底记录“原图真实尺寸”：画布上展示的是 Supabase 缩略图，其 naturalWidth 并非原图尺寸；
  // 正常情况下后端已把真实尺寸写进 generated.size（见 inngest image-create/edit），此处仅在缺失时
  // （老节点 / 后端未解析出）解析原图文件头补齐，写入 generated.size + sizeUrl。只在进入视口后执行。
  useEffect(() => {
    if (!mainUrl || !shouldRenderImage) return;
    const g = (data as any)?.generated ?? {};
    const haveReal =
      g?.sizeUrl === mainUrl &&
      typeof g?.size === 'string' &&
      /^\d+x\d+$/i.test(g.size);
    if (haveReal) return;
    const ctrl = new AbortController();
    (async () => {
      const size = await readImageSizeFromUrl(mainUrl, { signal: ctrl.signal });
      if (!size || ctrl.signal.aborted) return;
      const latest = (() => { try { return getNode(id) as any; } catch { return undefined; } })();
      const prevG = (latest?.data?.generated ?? g ?? {}) as any;
      const sz = `${size.width}x${size.height}`;
      if (prevG?.size === sz && prevG?.sizeUrl === mainUrl) return;
      // 同步把真实尺寸回写进当前激活的历史条目（绑定其 url），让老节点/后端漏解析的条目也带上真实尺寸，
      // 之后切换历史可直接读取、无需再次解析文件头。
      const prevHistory = (latest?.data?.history ?? (data as any)?.history) as any[] | undefined;
      const activeId = (latest?.data?.activeHistoryId ?? (data as any)?.activeHistoryId) as string | undefined;
      const nextHistory = patchHistoryItemById(prevHistory, activeId, mainUrl, { size: sz });
      updateNodeData(id, {
        generated: { ...prevG, size: sz, sizeUrl: mainUrl },
        ...(nextHistory !== prevHistory ? { history: nextHistory } : {}),
      });
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    mainUrl,
    shouldRenderImage,
    updateNodeData,
    getNode,
    (data as any)?.generated?.sizeUrl,
    (data as any)?.generated?.size,
  ]);
  const multiUrls = useMemo(() => (Array.isArray(generatedUrls) ? generatedUrls.filter(Boolean) : []), [generatedUrls]);
  const multiThumbHeightPx = useMemo(() => {
    try {
      const h = Number(measuredHeight ?? 240);
      const quarter = Math.floor(h / 4);
      // 兜底：不要太小；也不要因为节点很大导致缩略图过于夸张
      return Math.max(72, Math.min(240, Number.isFinite(quarter) ? quarter : 72));
    } catch {
      return 72;
    }
  }, [measuredHeight]);
  const multiPicker = useMultiImagePicker({
    urls: multiUrls,
    activeUrl: mainUrl,
    defaultThumbAspectRatio,
    thumbHeightPx: multiThumbHeightPx,
    onSelectUrl: (url) => updateNodeData(id, { generated: { ...(data as any).generated, url } }),
  });
  const guessImageMimeType = useCallback((u?: string): string | undefined => {
    try {
      const s = String(u || '').trim().toLowerCase();
      if (!s) return undefined;
      // Strip query/hash.
      const clean = s.split('#')[0]?.split('?')[0] ?? s;
      if (clean.endsWith('.jpg') || clean.endsWith('.jpeg')) return 'image/jpeg';
      if (clean.endsWith('.png')) return 'image/png';
      if (clean.endsWith('.webp')) return 'image/webp';
      if (clean.endsWith('.gif')) return 'image/gif';
      if (clean.endsWith('.avif')) return 'image/avif';
      return undefined;
    } catch {
      return undefined;
    }
  }, []);
  const handleCreateNodeFromThumb = useCallback(
    (url: string) => {
      try {
        if (isPreview) return;
        const u = String(url || '').trim();
        if (!u) return;

        const cur = getNode(id) as any;
        const baseX = cur?.position?.x ?? 0;
        const baseY = cur?.position?.y ?? 0;
        const width = (cur?.measured?.width ?? 300) as number;
        const gap = 120;
        const position = { x: baseX + width + gap, y: baseY };

        const imageType =
          guessImageMimeType(u) ||
          ((data as any)?.generated?.type as string | undefined) ||
          'image/png';

        const newId = addNode('image', {
          position,
          selected: true,
          data: {
            viewMode: 'transform',
            model: modelId,
            size: (data as any)?.size,
            instructions: data.instructions ?? '',
            generated: { url: u, type: imageType },
            description: (data as any)?.description,
            updatedAt: new Date().toISOString(),
          },
        });
        if (!newId) return;

        requestAnimationFrame(() => {
          try {
            setNodes((prev: any[]) =>
              prev.map((n: any) => ({
                ...n,
                selected: String(n.id) === String(newId),
              }))
            );
          } catch {}
        });

        try {
          toast.success(t('Created new node'));
        } catch {}
      } catch (error) {
        handleError(t('Failed to create node'), error);
      }
    },
    [addNode, data, getNode, guessImageMimeType, id, isPreview, modelId, setNodes, t]
  );
  const handleCreateNodesFromThumbs = useCallback(
    (urls: string[]) => {
      try {
        if (isPreview) return;
        const list = (Array.isArray(urls) ? urls : [])
          .map((u) => String(u || '').trim())
          .filter(Boolean);
        if (!list.length) return;

        const cur = getNode(id) as any;
        const baseX = cur?.position?.x ?? 0;
        const baseY = cur?.position?.y ?? 0;
        const width = (cur?.measured?.width ?? 300) as number;
        const height = (cur?.measured?.height ?? 240) as number;
        const gap = 120;
        const verticalGap = 12;

        const created: string[] = [];
        list.forEach((u, idx) => {
          const position = {
            x: baseX + width + gap,
            y: baseY + idx * (height + verticalGap),
          };
          const imageType =
            guessImageMimeType(u) ||
            ((data as any)?.generated?.type as string | undefined) ||
            'image/png';
          const newId = addNode('image', {
            position,
            selected: idx === 0,
            data: {
              viewMode: 'transform',
              model: modelId,
              size: (data as any)?.size,
              instructions: data.instructions ?? '',
              generated: { url: u, type: imageType },
              description: (data as any)?.description,
              updatedAt: new Date().toISOString(),
            },
          });
          if (newId) created.push(String(newId));
        });
        if (!created.length) return;

        requestAnimationFrame(() => {
          try {
            const first = created[0];
            setNodes((prev: any[]) =>
              prev.map((n: any) => ({
                ...n,
                selected: String(n.id) === String(first),
              }))
            );
          } catch {}
        });

        try {
          if (created.length === 1) toast.success(t('Created new node'));
          else toast.success(t('Created {{count}} nodes', { count: created.length }));
        } catch {}
      } catch (error) {
        handleError(t('Failed to create node'), error);
      }
    },
    [addNode, data, getNode, guessImageMimeType, id, isPreview, modelId, setNodes, t]
  );
  const isGlbLikeUrl = useCallback((u?: string) => {
    try {
      const s = String(u || '').trim();
      if (!s) return false;
      return /\.(glb|gltf)(\?|#|$)/i.test(s);
    } catch {
      return false;
    }
  }, []);
  const isMainModel = useMemo(() => isGlbLikeUrl(mainUrl), [isGlbLikeUrl, mainUrl]);
  // WebGL preview + NodeSurface "focus inset" animation can cause flicker while resizing.
  // For GLB outputs, keep a stable inset to avoid repeated resizes during hover/selection transitions.
  const insetClass = isMainModel ? 'inset-0' : insetClassBase;
  // 多图菜单：当节点折叠时自动收起
  useEffect(() => {
    if (isCollapsed && multiPicker.open) multiPicker.setOpen(false);
  }, [isCollapsed, multiPicker]);

  // 多图比对预览：在主节点 DOM 旁渲染等尺寸的图片瓦片（不创建节点，仅 DOM 覆盖层）
  // - 不进入画布持久化 / 撤销栈 / Yjs 同步
  // - 瓦片右上角「+ 节点」按钮：调用 handleCreateNodeFromThumb 转为正式 image 节点
  const compareUrls = useMemo(
    () => multiUrls.filter((u) => u && u !== mainUrl),
    [multiUrls, mainUrl],
  );
  const showComparePreview =
    !isPreview && multiPicker.open && !isCollapsed && compareUrls.length > 0 && Boolean(mainUrl);
  const tileWidthPx = useMemo(() => {
    const w = Number(measuredWidth);
    return Number.isFinite(w) && w > 0 ? w : 300;
  }, [measuredWidth]);
  const tileHeightPx = useMemo(() => {
    const h = Number(measuredHeight);
    return Number.isFinite(h) && h > 0 ? h : 240;
  }, [measuredHeight]);
  // 画布展示用 media 服务预生成的缩略图（缺失时回退原图）；节点数据里仍保存原图 URL。
  // 3D models should not go through image thumbnails.
  const { src: canvasMainUrl, onError: onCanvasMainError } = useImageVariant(
    isMainModel ? undefined : mainUrl,
    { height: 512, resize: 'contain', quality: 75 },
  );
  const displayMainUrl = isMainModel ? mainUrl : canvasMainUrl;
  // 注意：这里的 displayMainUrl 可能是缩略图（短边 512），但保持宽高比即可用于 UI 调整节点比例
  const urlForParseTop =
    (Array.isArray((data as any)?.generated?.urls) && (data as any)?.generated?.urls?.[0]) ||
    (data as any)?.generated?.url ||
    (data as any)?.content?.url;
  const hasDescription = Boolean((data as any)?.description && String((data as any).description).trim().length > 0);
  const [showDescDialog, setShowDescDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // Reframe model selection state moved to ImageTopToolbar
  
  // titleBubble 为空（序号显示在图片上）
  const titleBubble = null;


  // 顶部工具栏逻辑统一在 ImageTopToolbar 中

  // 顶部工具栏“预览”按钮事件
  useEffect(() => {
    const onOpenPreview = (e: any) => {
      try {
        if (e?.detail?.id !== id) return;
        setPreviewOpen(true);
      } catch {}
    };
    window.addEventListener('open_image_preview', onOpenPreview as any);
    return () => window.removeEventListener('open_image_preview', onOpenPreview as any);
  }, [id]);

  return (
    <>
    {/* 顶部工具栏：只在选中时挂载，避免每个节点都订阅 selection/nodes */}
    {isSelected && !isCollapsed ? (
      <ImageTopToolbar
        nodeId={id}
        imageUrl={
          isMainModel
            ? (urlForParseTop && !isGlbLikeUrl(urlForParseTop) ? urlForParseTop : undefined)
            : (mainUrl || urlForParseTop)
        }
        data={data}
        isToolbarBlocked={isToolbarBlocked}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        isDescribing={isDescribing}
        hasDescription={hasDescription}
        onShowDescription={() => setShowDescDialog(true)}
        onParse={async () => {
          if (!urlForParseTop || !project?.id) return;
          try {
            setIsDescribing(true);
            const res = await describeAction(urlForParseTop, project.id);
            if ('error' in res) throw new Error(res.error);
            updateNodeData(id, {
              description: res.description,
              updatedAt: new Date().toISOString(),
            });
            setShowDescDialog(true);
          } catch (error) {
            handleError(t('Failed to parse image'), error);
          } finally {
            setIsDescribing(false);
          }
        }}
      />
    ) : null}
    <NodeLayout
      id={id}
      data={data}
      type={type}
      title={title}
      variant="modern"
      aspectRatio={aspectRatio}
      toolbar={toolbar}
      titleBubble={titleBubble}
      className={multiPicker.open ? 'overflow-visible' : undefined}
      collapsedAspectRatio="16/9"
      collapsedSummary={<ImageCollapsedSummary data={data as any} />}
    >
      {/* 多图：DOM 覆盖层渲染等尺寸图片瓦片（不创建节点），随画布缩放 */}
      {showComparePreview ? (
        <ImageComparePreview
          tiles={compareUrls}
          tileWidthPx={tileWidthPx}
          tileHeightPx={tileHeightPx}
          menuRef={multiPicker.menuRef}
          onSetMain={(u) => multiPicker.selectUrl(u)}
          onSplit={(u) => handleCreateNodeFromThumb(u)}
          t={t}
        />
      ) : null}
      {isProcessing && (
        <div>
          <ProcessingSurface
            aspectRatio={aspectRatio}
            bgStyle={bgStyle}
            insetClass={insetClass}
            radiusClass="rounded-3xl"
            progress={progress}
            icon={<ImageIcon />}
            label={<div className="text-base font-medium text-muted-foreground text-center">{progress < 100 ? t('Generating image… {{percent}}%', { percent: Number(progress).toFixed(2) }) : t('Generating image…')}</div>}
            footer={<div className="text-sm text-muted-foreground/80 text-center">{Number(progress) >= 99 ? (
              <div className="opacity-80"><div>{t('eta_hint.line1')}</div><div>{t('eta_hint.line2')}</div><div>{t('eta_hint.line3')}</div></div>
            ) : (
              Number(progress) < 99 ? (
                <div className="opacity-80">{hasEstimate ? t('Estimated wait {{time}}', { time: remainingText }) : t('Calculating wait time…')}</div>
              ) : null
            )}</div>}
          />
        </div>
      )}
      {isProcessing && (
        <>
          <div className="sr-only" aria-live="polite">{t('Generating image… {{percent}}%', { percent: Number(progress).toFixed(2) })}</div>
          <div className="sr-only" aria-live="polite">{hasEstimate ? t('About {{time}} left', { time: remainingText }) : t('Calculating wait time…')}</div>
          <div className="hidden" data-progress-aria>{Number(progress).toFixed(2)}%</div>
        </>
      )}
      {!isProcessing && loading && (
        <div>
          <NodeSurface aspectRatio={aspectRatio} bgStyle={bgStyle} showDashed={false} radiusClass="rounded-3xl" insetClass={insetClass}>
            <div className="relative flex h-full items-center justify-center">
              <Loader2Icon size={24} className="size-6 animate-spin text-muted-foreground" />
            </div>
          </NodeSurface>
        </div>
      )}
      {isFailed && !loading && (
        <div>
          <NodeSurface aspectRatio={aspectRatio} bgStyle={bgStyle} showDashed={false} radiusClass="rounded-3xl" insetClass={insetClass}>
            <div className="relative flex h-full items-center justify-center p-4">
              <div className="text-center max-w-[90%]">
                <p className="inline-flex flex-wrap items-center justify-center gap-2 text-sm font-medium text-foreground">
                  <span>{t('Image generation failed')}</span>
                  <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                    {t('Credits refunded')}
                  </span>
                </p>
                {(data as any)?.error ? (
                  <p className="mt-1 text-xs text-muted-foreground break-words whitespace-pre-wrap">{String((data as any).error)}</p>
                ) : null}
              </div>
            </div>
          </NodeSurface>
        </div>
      )}
      {!isProcessing && !isFailed && !loading && !mainUrl && (
        <div className="relative">
          <NodeSurface aspectRatio={aspectRatio} bgStyle={bgStyle} showDashed={false} radiusClass="rounded-3xl" insetClass={insetClass}>
            <div className="relative flex h-full items-center justify-center p-6">
              <div
                className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none"
              >
                <div className="flex flex-col items-center gap-2 text-center max-w-md">
                  {/* 保留现有提示 */}
                  <p className="text-lg font-medium text-foreground">{t('Ready to Create?')}</p>
                  <p className="text-base text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Zap size={14} className="opacity-80" />
                      {t('Click Generate to create an image')}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </NodeSurface>

          {/* 引导按钮固定在外层容器底部，脱离 NodeSurface 的 inset 过渡，选中时不会产生滑动 */}
          {isSelected && !isMultiSelect && modelCapabilities.needsImage ? (
            <div className="pointer-events-auto absolute inset-x-0 bottom-6 flex justify-center px-6">
              <ImageGuidance
                guidance={modelCapabilities}
                onAddImageNode={handleAddImageNode}
                t={t}
              />
            </div>
          ) : null}
        </div>
      )}
      {!isProcessing && !isFailed && !loading && (generatedUrl || (generatedUrls && generatedUrls.length > 0)) && (
        <div ref={imgOutputRef}>
          <NodeSurface aspectRatio={aspectRatio} bgStyle={bgStyle} showDashed={false} radiusClass="rounded-3xl" insetClass={insetClass}>
            {!shouldRenderImage ? (
              <Skeleton className="absolute inset-0 rounded-none" />
            ) : (<>
            {multiUrls.length > 1 ? (
              <MultiImageTrigger
                state={multiPicker}
                ariaLabel={t('Images')}
                widthPx={100}
                onTriggerClick={() => {
                  try {
                    // 进入多图对比预览时清空所有选中，避免节点工具栏 / 提示词输入条挡住对比视图
                    setNodes((prev: any[]) =>
                      prev.map((n: any) =>
                        (n as any)?.selected ? { ...n, selected: false } : n
                      )
                    );
                  } catch {}
                }}
              />
            ) : null}
            {isMainModel && mainUrl && !(Array.isArray(generatedUrls) && generatedUrls.length > 1) ? (
              <div
                className="absolute inset-0 bg-neutral-950 cursor-zoom-in"
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPreviewOpen(true);
                }}
                title={t('Double click to zoom') || 'Double click to zoom'}
              >
                <div className="absolute bottom-3 left-3 pointer-events-none z-10">
                  <div className="text-xs sm:text-sm text-white/80 drop-shadow-none">
                    {t('Double click to zoom')}
                  </div>
                </div>
                <GlbViewer url={mainUrl} interactive={false} className="absolute inset-0" />
              </div>
            ) : (
              <PreviewImageOnClick className="absolute inset-0" onPreview={() => setPreviewOpen(true)} enabled={isSelected}>
                {Array.isArray(generatedUrls) && generatedUrls.length > 1 ? (
                  <>
                    <Image
                      src={(displayMainUrl || mainUrl) as string}
                      unoptimized={isPregeneratedThumbnailUrl((displayMainUrl || mainUrl) as string)}
                      alt={t('Generated image')}
                      width={1000}
                      height={1000}
                      priority={false}
                      loading="lazy"
                      onLoad={() => setImgLoaded(true)}
                      onError={onCanvasMainError}
                      className={`w-full h-full object-cover cursor-zoom-in transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
                    />
                    {!imgLoaded && (
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <p className="text-xs text-muted-foreground">
                          {t('Image generated successfully. Downloading image...')}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <EditWithPinturaOnDoubleClick
                    src={(mainUrl as string)}
                    className="absolute inset-0"
                    onUploadingChange={(u) => setLoading(u)}
                    onUpdated={({ url, width, height, size }) => {
                      const latestNode = (() => { try { return getNode(id) as any; } catch { return undefined; } })();
                      const prevG = (latestNode?.data?.generated ?? (data as any)?.generated ?? {}) as any;
                      const patchG: any = { ...prevG, url, type: 'image/png' };
                      const sz =
                        (typeof size === 'string' && /^\d+x\d+$/i.test(size) ? size : undefined) ||
                        (Number.isFinite(width) && Number.isFinite(height) && (width as number) > 0 && (height as number) > 0
                          ? `${width}x${height}`
                          : undefined);
                      if (sz) {
                        // 真实尺寸统一存于 generated.size（"WxH"）+ sizeUrl。
                        patchG.size = sz;
                        patchG.sizeUrl = url;
                      }
                      updateNodeData(id, {
                        generated: patchG,
                        content: { url, type: 'image/png' },
                        sourceUrl: url,
                        updatedAt: new Date().toISOString(),
                      });
                      toast.success(t('Image updated'));
                    }}
                  >
                  <Image
                    src={(displayMainUrl || mainUrl) as string}
                    unoptimized={isPregeneratedThumbnailUrl((displayMainUrl || mainUrl) as string)}
                    alt={t('Generated image')}
                    width={1000}
                    height={1000}
                    priority={false}
                    loading="lazy"
                    onLoad={() => setImgLoaded(true)}
                    onError={onCanvasMainError}
                    className={`w-full h-full object-cover cursor-zoom-in transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
                  />
                  {!imgLoaded && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <p className="text-xs text-muted-foreground">
                        {t('Image generated successfully. Downloading image...')}
                      </p>
                    </div>
                  )}
                  </EditWithPinturaOnDoubleClick>
                )}
              </PreviewImageOnClick>
            )}
            {(data as any)?.inpaintMask ? (
              <div className="absolute bottom-2 right-2 z-10 pointer-events-none">
                <div className="h-6 px-2 rounded-xl border border-primary/20 bg-primary/10 backdrop-blur-md text-[10px] font-bold tracking-wider text-primary flex items-center justify-center shadow-none ring-1 ring-white/10">
                  {t('Mask exists')}
                </div>
              </div>
            ) : null}
            {(() => {
              // partial 成功标识：completed 状态下产出张数 < 请求张数 → 显示"成功 N/M"提示，用户已被自动按比例退款。
              if ((data as any)?.status !== 'completed') return null;
              const requested = Number((data as any)?.imagesCount ?? 0);
              if (!Number.isFinite(requested) || requested <= 1) return null;
              const produced = Array.isArray(generatedUrls) && generatedUrls.length > 0
                ? generatedUrls.length
                : (generatedUrl ? 1 : 0);
              if (produced === 0 || produced >= requested) return null;
              const failed = requested - produced;
              return (
                <div className="absolute bottom-2 right-2 z-10 pointer-events-none">
                  <div className="h-7 px-2.5 rounded-xl border border-amber-500/60 bg-amber-500 text-[11px] font-bold tracking-wide text-white shadow-md flex items-center justify-center whitespace-nowrap">
                    {t('Success {{produced}}, failed {{failed}}, refunded proportionally', { produced, failed })}
                  </div>
                </div>
              );
            })()}
            </>)}
          </NodeSurface>
        </div>
      )}
    </NodeLayout>
    {isMainModel && mainUrl && !(Array.isArray(generatedUrls) && generatedUrls.length > 1) ? (
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-4xl p-4">
          <DialogHeader className="pr-8">
            <DialogTitle>{t('3D Preview') || '3D Preview'}</DialogTitle>
          </DialogHeader>
          <div className="w-full h-[70vh] bg-neutral-950 rounded-xl overflow-hidden border border-neutral-800">
            <GlbViewer url={mainUrl} className="h-full w-full" />
          </div>
        </DialogContent>
      </Dialog>
    ) : (
      <ImagePreviewDialog 
        open={previewOpen} 
        onOpenChange={setPreviewOpen} 
        url={mainUrl} 
        urls={Array.isArray((data as any)?.generated?.urls) ? (data as any).generated.urls : undefined}
        alt={t('Preview image')} 
        titleClose={t('Close preview')} 
      />
    )}
    <TextModeImageDialog
      open={t2iImageDialogOpen}
      onOpenChange={setT2iImageDialogOpen}
      mediaKind="image"
      onKeep={() => { void runGenerate(); }}
      onSwitch={() => {
        updateNodeData(id, { imageMode: 'i2i' });
        void runGenerate({ imageMode: 'i2i' });
      }}
    />
    <PrecheckDialog
      missing={precheckMissing}
      onOpenChange={(open) => { if (!open) setPrecheckMissing(null); }}
      action={precheckMissing === 'image'
        ? { label: t('Add image'), onClick: () => { try { handleAddImageNode(); } catch {} } }
        : undefined}
    />
    <DescriptionDialog
      open={showDescDialog}
      onOpenChange={setShowDescDialog}
      text={String((data as any)?.description || '')}
    />
    <SubjectPickerDialog
      open={subjectPickerOpen}
      onOpenChange={setSubjectPickerOpen}
      onPick={handlePickFromSubjectLibrary}
    />
    </>
  );
});
ImageTransform.displayName = 'ImageTransform';